import { __assign, __awaiter, __generator, __rest } from "tslib";
import axios from 'axios';
import throttle from 'lodash/throttle';
import { all, takeEvery, take, call, put, select, actionChannel, } from 'redux-saga/effects';
import { buffers, eventChannel, END } from 'redux-saga';
import { Url, URLS } from '@wix/da-url';
import { ConfigSelectors } from '../../config';
import { getCsrfToken } from '../../publicSession/selectors';
import { uploadFile, uploadFailure, uploadSuccess, uploadProgress, uploadFiles, uploadFileImmediately, } from './actions';
import { getUploads, getUploadsWaiting } from './selectors';
import { isApiError, request } from '../../xhr';
var THROTTLE_WAIT = 500;
var MAXIMUM_CONCURRENT_UPLOADS = 10;
var DAPI_ROOT = Url.buildFromPath('dapi/v1', URLS.ROOT);
var fakeDeviationFetch = function (options) {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line
        var deviationsByType = require('@wix/da-sample-data').deviationsByType;
        return __assign(__assign({}, deviationsByType.image[0]), { deviationId: options.params.deviationid });
    }
};
// If you are hopelessly trying to find out why uploads do not work on dev node
// It's because dev node runs on http and dapi is https. Just give up and test on staging
function dapiPost(endpoint, data, options) {
    return __awaiter(this, void 0, void 0, function () {
        var onSuccess, onError, axiosOptions, response, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    onSuccess = options.onSuccess, onError = options.onError, axiosOptions = __rest(options, ["onSuccess", "onError"]);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, axios.request(__assign(__assign({ method: 'post', baseURL: DAPI_ROOT, url: "/".concat(endpoint) }, axiosOptions), { data: data }))];
                case 2:
                    response = _a.sent();
                    if (response.data.error) {
                        onError && onError(__assign(__assign({}, response.data), { isServerError: true }));
                        return [2 /*return*/, false];
                    }
                    onSuccess && onSuccess(response);
                    return [2 /*return*/, response.data];
                case 3:
                    error_1 = _a.sent();
                    onError && onError(error_1);
                    return [2 /*return*/, false];
                case 4: return [2 /*return*/];
            }
        });
    });
}
function createUploadFileChannel(params) {
    return eventChannel(function (emitter) {
        var source = axios.CancelToken.source();
        var throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        dapiPost('submit/upload', params, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            withCredentials: true,
            cancelToken: source.token,
            onUploadProgress: function (e) {
                if (e.lengthComputable) {
                    var progress = e.loaded / e.total;
                    throttledEmitter({ progress: progress });
                }
            },
            onSuccess: function (response) {
                emitter({ success: true, response: response });
            },
            onError: function (err) {
                var _a;
                var error = new Error((_a = err.error_description) !== null && _a !== void 0 ? _a : 'Upload failed');
                if (err.isServerError) {
                    error['isServerError'] = true;
                }
                emitter({
                    error: error,
                });
                emitter(END);
            },
        });
        return function () {
            source.cancel();
        };
    }, buffers.sliding(2));
}
var fakeDeviationId = 123;
// Simulate file upload channel events
function createFakeUploadFileChannel(params) {
    return eventChannel(function (emitter) {
        var throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        var progress = 0;
        var interval = setInterval(function () {
            progress = Math.min(1, progress);
            throttledEmitter({ progress: progress });
            if (progress < 1) {
                return (progress += Math.random() / 40);
            }
            if (window.doError) {
                emitter({ error: new Error('Test error') });
                emitter(END);
                return;
            }
            var response = { data: { content: { privateid: fakeDeviationId++ } } };
            emitter({ progress: 1 });
            emitter({ success: true, response: response });
            emitter(END);
        }, 50);
        return function () {
            clearInterval(interval);
        };
    });
}
function createReduxChannel(_a) {
    var formData, _b, _c, _d, doFake;
    var file = _a.file, folderid = _a.folderid, folderName = _a.folderName, isNewSubmit = _a.isNewSubmit;
    return __generator(this, function (_e) {
        switch (_e.label) {
            case 0:
                formData = new FormData();
                formData.append('file', file, file.name);
                folderid && formData.append('folderid', folderid);
                // if no folderid is specified, the folder name must be set to "Saved Submissions" as per endpoint spec
                // except stash protect which is a special peanut
                !folderid &&
                    formData.append('folder_name', folderName !== null && folderName !== void 0 ? folderName : 'Saved Submissions');
                // we only support uploading to stash
                formData.append('action', 'StashPost');
                if (isNewSubmit) {
                    formData.append('is_new_dapi_submit', 'true');
                }
                _c = (_b = formData).append;
                _d = ['_csrf'];
                return [4 /*yield*/, select(getCsrfToken)];
            case 1:
                _c.apply(_b, _d.concat([_e.sent()]));
                if (!(process.env.NODE_ENV !== 'production')) return [3 /*break*/, 3];
                return [4 /*yield*/, select(ConfigSelectors.key('fakeApiRequests'))];
            case 2:
                doFake = _e.sent();
                if (doFake) {
                    return [2 /*return*/, createFakeUploadFileChannel(formData)];
                }
                _e.label = 3;
            case 3: return [2 /*return*/, createUploadFileChannel(formData)];
        }
    });
}
function uploadFileHandler(action) {
    var _a, file, fileId, folderId, folderName, autoFetchDeviationOnSuccess, isNewSubmit, onSuccess, onFailure, channel, _b, _c, progress, error, success, response, deviation, deviationId, params, result;
    var _d, _e;
    return __generator(this, function (_f) {
        switch (_f.label) {
            case 0:
                _a = action.payload, file = _a.file, fileId = _a.fileId, folderId = _a.folderId, folderName = _a.folderName, autoFetchDeviationOnSuccess = _a.autoFetchDeviationOnSuccess, isNewSubmit = _a.isNewSubmit, onSuccess = _a.onSuccess, onFailure = _a.onFailure;
                return [4 /*yield*/, call(createReduxChannel, {
                        file: file,
                        folderid: folderId,
                        folderName: folderName,
                        isNewSubmit: isNewSubmit,
                    })];
            case 1:
                channel = _f.sent();
                _f.label = 2;
            case 2:
                if (!true) return [3 /*break*/, 15];
                return [4 /*yield*/, take(channel)];
            case 3:
                _b = _f.sent(), _c = _b.progress, progress = _c === void 0 ? 0 : _c, error = _b.error, success = _b.success, response = _b.response;
                if (!error) return [3 /*break*/, 7];
                return [4 /*yield*/, put(uploadFailure(fileId, error))];
            case 4:
                _f.sent();
                if (!onFailure) return [3 /*break*/, 6];
                return [4 /*yield*/, call(onFailure, { fileId: fileId })];
            case 5:
                _f.sent();
                _f.label = 6;
            case 6: return [2 /*return*/];
            case 7:
                if (!success) return [3 /*break*/, 13];
                return [4 /*yield*/, put(uploadSuccess(fileId))];
            case 8:
                _f.sent();
                if (!onSuccess) return [3 /*break*/, 12];
                deviation = void 0;
                deviationId = (_e = (_d = response === null || response === void 0 ? void 0 : response.data) === null || _d === void 0 ? void 0 : _d.content) === null || _e === void 0 ? void 0 : _e.privateid;
                if (!(autoFetchDeviationOnSuccess && deviationId)) return [3 /*break*/, 10];
                params = {
                    deviationid: deviationId,
                    type: 'art',
                };
                return [4 /*yield*/, call(request, {
                        method: 'get',
                        url: '/deviation/fetch',
                        params: params,
                    }, fakeDeviationFetch)];
            case 9:
                result = _f.sent();
                if (result && !isApiError(result)) {
                    deviation = result;
                }
                _f.label = 10;
            case 10: return [4 /*yield*/, call(onSuccess, {
                    fileId: fileId,
                    file: file,
                    deviationId: deviationId,
                    deviation: deviation,
                })];
            case 11:
                _f.sent();
                _f.label = 12;
            case 12: return [2 /*return*/];
            case 13: return [4 /*yield*/, put(uploadProgress(fileId, progress))];
            case 14:
                _f.sent();
                return [3 /*break*/, 2];
            case 15: return [2 /*return*/];
        }
    });
}
function uploadQueueHandler(action) {
    var uploads, activeUploads, openUploadSlots, waitingUploads;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, select(getUploads)];
            case 1:
                uploads = _a.sent();
                activeUploads = uploads.filter(function (upload) { return !upload.error; });
                openUploadSlots = MAXIMUM_CONCURRENT_UPLOADS - activeUploads.length;
                if (openUploadSlots <= 0) {
                    // do nothing, we have no space to queue more
                    return [2 /*return*/];
                }
                return [4 /*yield*/, select(getUploadsWaiting)];
            case 2:
                waitingUploads = _a.sent();
                // start downloading the appropriate amount
                return [4 /*yield*/, all(waitingUploads
                        .slice(0, openUploadSlots)
                        .map(function (upload) { return put(uploadFileImmediately(upload)); }))];
            case 3:
                // start downloading the appropriate amount
                _a.sent();
                return [2 /*return*/];
        }
    });
}
function uploadQueueWatcher() {
    var channel, action;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, actionChannel([
                    uploadFile,
                    uploadFiles,
                    uploadSuccess,
                    uploadFailure,
                ])];
            case 1:
                channel = _a.sent();
                _a.label = 2;
            case 2:
                if (!true) return [3 /*break*/, 5];
                return [4 /*yield*/, take(channel)];
            case 3:
                action = _a.sent();
                return [4 /*yield*/, call(uploadQueueHandler, action)];
            case 4:
                _a.sent();
                return [3 /*break*/, 2];
            case 5: return [2 /*return*/];
        }
    });
}
export default function saga() {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, all([
                    takeEvery(uploadFileImmediately, uploadFileHandler),
                    // update the queue every time something relevant happens
                    uploadQueueWatcher(),
                ])];
            case 1:
                _a.sent();
                return [2 /*return*/];
        }
    });
}
//# sourceMappingURL=saga.js.map